"use client";
// "use client" hoja interactiva: form con estado + POST + toast — isla, no se migra a server.

import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/use-i18n";
import { cn } from "@/lib/utils";

/**
 * Public opt-in for important product updates.
 * Admin decides every email content from the admin Newsletter tab — no auto blasts.
 */
export function NewsletterSignup({
  className,
  /** En columnas angostas (footer) apila input + botón a ancho completo. */
  stacked = false,
}: {
  className?: string;
  stacked?: boolean;
}) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    // Honeypot: bots fill it — fake success without hitting the API.
    if (honeypot) {
      toast.success(t("newsletterSuccess"));
      setEmail("");
      setHoneypot("");
      return;
    }
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      const msg = t("newsletterInvalidEmail");
      setFieldError(msg);
      toast.error(msg);
      // P2 a11y: mover foco al campo inválido para lectores de pantalla.
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ email: trimmed, honeypot }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        alreadySubscribed?: boolean;
        error?: string;
      };
      if (!res.ok) {
        throw new Error(data.error || "fail");
      }
      if (data.alreadySubscribed) {
        toast.message(t("newsletterAlready"));
      } else {
        toast.success(t("newsletterSuccess"));
      }
      setEmail("");
      setHoneypot("");
      setFieldError(null);
    } catch {
      toast.error(t("newsletterError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      className={className}
      data-testid="newsletter-signup"
      aria-label={t("newsletterTitle")}
    >
      <p className="text-sm font-medium text-foreground">
        {t("newsletterTitle")}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
        {t("newsletterBody")}
      </p>
      {/* Honeypot anti-spam: hidden from users, bots fill it. Validated server-side (max 0). */}
      <input
        type="text"
        name="honeypot"
        value={honeypot}
        onChange={(ev) => setHoneypot(ev.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      <div
        className={
          stacked
            ? "mt-3 flex flex-col gap-2"
            : "mt-3 flex flex-col gap-2 sm:flex-row"
        }
      >
        <label htmlFor="newsletter-email" className="sr-only">
          {t("newsletterPlaceholder")}
        </label>
        <Input
          id="newsletter-email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(ev) => {
            setEmail(ev.target.value);
            if (fieldError) setFieldError(null);
          }}
          placeholder={t("newsletterPlaceholder")}
          aria-label={t("newsletterPlaceholder")}
          aria-invalid={fieldError ? true : undefined}
          aria-describedby={fieldError ? "newsletter-error" : undefined}
          ref={inputRef}
          // 2026-09-25 FASE 3 FIX design-docs-auth (research 2026 DSP radius
          // tokens: --radius-lg → --radius-field 16px SSOT, paridad inputs
          // AuthForm).
          className="min-h-11 flex-1 rounded-[var(--radius-field)] bg-[var(--background-elevated)] px-4 text-sm shadow-xs"
          disabled={loading}
          data-testid="newsletter-email"
        />
        <Button
          type="submit"
          disabled={loading}
          data-testid="newsletter-submit"
          className={cn(
            // 2026-09-25 RUN1 FASE3-FIX (SEED=2026-09-25-c6c873bf; research 2026
            // DSP radius tokens: botón → --radius-button pill SSOT; input conserva
            // --radius-field 16px, paridad AuthForm).
            "min-h-11 rounded-[var(--radius-button)] bg-[var(--accent)] px-5 text-sm font-medium text-[var(--accent-foreground)] shadow-xs transition-[background-color,transform] duration-200 hover:bg-[var(--accent-deep)] active:scale-[0.98]",
            stacked ? "w-full" : "w-full sm:w-auto",
          )}
        >
          {loading ? (
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          ) : (
            t("newsletterCta")
          )}
        </Button>
      </div>
      {fieldError ? (
        <p
          id="newsletter-error"
          role="alert"
          className="mt-2 text-xs text-[var(--danger-text)]"
        >
          {fieldError}
        </p>
      ) : null}
    </form>
  );
}
